import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import Copyright from './copyright';


/**
 * ContentDialog - A reusable dialog component that can wrap any content
 *
 * @param {Object} props
 * @param {boolean} props.open - Controls whether the dialog is open
 * @param {Function} props.onClose - Callback when the dialog is closed
 * @param {React.ReactNode} props.children - The content to display in the dialog
 * @param {string} [props.title] - Title for the dialog
 * @param {string} [props.subtitle] - Optional subtitle to display alongside the title
 * @param {React.ReactNode} [props.titleComponent] - Custom component to render instead of the default title
 * @param {React.ReactNode} [props.actions] - Custom actions for the dialog footer
 * @param {'xs' | 'sm' | 'md' | 'lg' | 'xl' | false} [props.maxWidth] - Max width of the dialog
 * @param {boolean} [props.fullWidth] - Whether the dialog should take up the full width
 * @param {boolean} [props.fullScreen] - Whether the dialog should be fullscreen
 * @param {boolean} [props.disableAutoFullScreen] - Disable automatic fullscreen on small screens
 * @param {boolean} [props.disableEscapeKeyDown] - Disable closing the dialog with Escape key
 * @param {boolean} [props.disableBackdropClick] - Disable closing the dialog when clicking the backdrop
 * @param {boolean} [props.hideCloseButton] - Hide the close button in the title
 * @param {boolean} [props.hideFooter] - Hide the footer completely
 * @param {Object} [props.contentProps] - Props to pass to the DialogContent component
 * @param {boolean} [props.dividers] - Whether to show dividers around the content
 * @param {string} [props.closeButtonText] - Text for the close button in footer
 */
const ContentDialog = ({
  open,
  onClose,
  children,
  title = "",
  subtitle = null,
  titleComponent = null,
  actions = null,
  maxWidth = "md",
  fullWidth = true,
  fullScreen = false,
  disableAutoFullScreen = false,
  disableEscapeKeyDown = false,
  disableBackdropClick = false,
  hideCloseButton = false,
  hideFooter = false,
  contentProps = {},
  dividers = true,
  closeButtonText = "Close"
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Determine if dialog should be fullscreen based on props and screen size
  const shouldBeFullScreen = fullScreen || (!disableAutoFullScreen && isSmallScreen);

  // Handle closing the dialog
  const handleClose = (event, reason) => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }

    if (onClose) {
      onClose(event, reason);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={shouldBeFullScreen}
      disableEscapeKeyDown={disableEscapeKeyDown}
      aria-labelledby="generic-dialog-title"
      scroll="paper"
    >
      {/* Custom title component or default title */}
      {titleComponent ? (
        titleComponent
      ) : (
        title && (
          <DialogTitle
            id="generic-dialog-title"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              pr: hideCloseButton ? 3 : 1,
              pb: subtitle ? 1 : 2
            }}
          >
            <Box>
              <Typography variant="h6" component="div">
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>

            {!hideCloseButton && (
              <IconButton
                aria-label="close"
                onClick={handleClose}
                size="small"
                sx={{ mt: -0.5 }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
        )
      )}

      {/* Dialog Content */}
      <DialogContent
        dividers={dividers}
        {...contentProps}
        sx={{
          ...(shouldBeFullScreen && { flexGrow: 1 }),
          ...(contentProps.sx || {})
        }}
      >
        {children || (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No content provided
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Dialog Actions - conditionally rendered */}
      {!hideFooter && (
        <>
          <DialogActions sx={{ px: 3, py: 2 }}>
            {actions || (
              <Button onClick={handleClose} variant="outlined">
                {closeButtonText}
              </Button>
            )}
          </DialogActions>

          {/* Copyright Element - centered at bottom */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              pb: 2
            }}
          >
            <Copyright />
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default ContentDialog;

// Example usage with TransactionViewer:
/*
import ContentDialog from './ContentDialog';
import { TransactionViewer } from './path/to/TransactionViewer';

function ParentComponent() {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedTransactions, setSelectedTransactions] = React.useState([]);

  const handleOpenDialog = (transactions) => {
    setSelectedTransactions(transactions);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Customized actions for the dialog footer
  const customActions = (
    <>
      <Button onClick={handleCloseDialog} color="inherit">
        Cancel
      </Button>
      <Button onClick={() => console.log('Export')} variant="contained" color="primary">
        Export Data
      </Button>
    </>
  );

  return (
    <>
      <Button
        onClick={() => handleOpenDialog(['transaction-1', 'transaction-2'])}
        variant="contained"
      >
        View Transactions
      </Button>

      <ContentDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        title="Transaction Details"
        subtitle={`Viewing ${selectedTransactions.length} transactions`}
        actions={customActions} // Optional custom actions
        // maxWidth="lg"  // Optional custom width
        // fullScreen={true}  // Optional fullscreen mode
      >
        <TransactionViewer transactionIDs={selectedTransactions} />
      </ContentDialog>
    </>
  );
}

// Example with custom content:
function AnotherComponent() {
  const [infoDialogOpen, setInfoDialogOpen] = React.useState(false);

  return (
    <>
      <Button onClick={() => setInfoDialogOpen(true)}>
        Show Information
      </Button>

      <ContentDialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        title="Important Information"
        dividers={false}
        hideFooter={true}
      >
        <Box sx={{ p: 2 }}>
          <Typography paragraph>
            This is some important information that needs to be displayed in a dialog.
          </Typography>
          <Typography paragraph>
            The dialog can contain any content - text, forms, tables, charts, etc.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => setInfoDialogOpen(false)}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </ContentDialog>
    </>
  );
}
*/